import React, { useState, useEffect } from 'react';
import { FileDown, Building2, User, Briefcase, MapPin, ChevronRight, ChevronLeft, Plus, Trash2, CheckCircle } from 'lucide-react';
import ExcelJS from 'exceljs';
import DateInput from './DateInput';
import './AppointmentOrderForm.css';

// Mock data - matching Form-XIII.xlsx template placeholders exactly
const mockFormData = {
  // Establishment fields
  est_name: "ABC Manufacturing Pvt Ltd",
  est_address: "123 Industrial Area, Whitefield, Bangalore - 560066",

  // Contractor fields
  contractor_name: "XYZ Contractors Pvt Ltd",
  contractor_address: "45 MG Road, Bangalore - 560001",

  // Employee array (Form XIII can have multiple employees)
  employees: [
    {
      emp_id: "EMP/2025/101",
      emp_name: "Rajesh Kumar",
      emp_lname: "Kumar",
      emp_age: "32",
      emp_gender: "Male",
      emp_desig: "Machine Operator",
      emp_address: "12 Gandhi Street, Bangalore - 560003",
      emp_perm_address: "Village Hosakote, District Bangalore Rural - 562114",
      emp_doj: "2025-01-15",
      emp_doe: "",
      emp_nature_work: "Operating CNC machines and quality control",
      emp_location_work: "Production Floor - Section A",
      emp_term_reason: ""
    },
    {
      emp_id: "EMP/2025/102",
      emp_name: "Anita Sharma",
      emp_lname: "Sharma",
      emp_age: "28",
      emp_gender: "Female",
      emp_desig: "Production Supervisor",
      emp_address: "67 Koramangala, Bangalore - 560034",
      emp_perm_address: "Plot 23, Electronic City, Bangalore - 560100",
      emp_doj: "2025-01-20",
      emp_doe: "",
      emp_nature_work: "Supervising production line and team management",
      emp_location_work: "Production Floor - Section B",
      emp_term_reason: ""
    }
  ]
};

const FormXIIIMinimumWages = () => {
  const [formData, setFormData] = useState(mockFormData);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Fetch data from API on component mount
    const fetchFormData = async () => {
      try {
        console.log('Fetching Form XIII data from API...');
        // const response = await fetch('/api/form-xiii-data');
        // const data = await response.json();
        // setFormData(data);
        console.log('Using mock data for Form XIII');
      } catch (error) {
        console.error('Error fetching Form XIII data:', error);
        console.log('Falling back to mock data');
      }
    };

    fetchFormData();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmployeeChange = (index, field, value) => {
    const updatedEmployees = [...formData.employees];
    updatedEmployees[index] = {
      ...updatedEmployees[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      employees: updatedEmployees
    }));
  };

  const addEmployee = () => {
    const newEmployee = {
      emp_id: "",
      emp_name: "",
      emp_lname: "",
      emp_age: "",
      emp_gender: "Male",
      emp_desig: "",
      emp_address: "",
      emp_perm_address: "",
      emp_doj: "",
      emp_doe: "",
      emp_nature_work: "",
      emp_location_work: "",
      emp_term_reason: ""
    };
    setFormData(prev => ({
      ...prev,
      employees: [...prev.employees, newEmployee]
    }));
  };

  const removeEmployee = (index) => {
    if (formData.employees.length > 1) {
      const updatedEmployees = formData.employees.filter((_, idx) => idx !== index);
      setFormData(prev => ({ ...prev, employees: updatedEmployees }));
      // If we're on a step beyond the new employee count, go back
      if (currentStep > updatedEmployees.length) {
        setCurrentStep(updatedEmployees.length);
      }
    }
  };

  const nextStep = () => {
    const totalSteps = 1 + formData.employees.length; // 1 (organization) + employees
    if (currentStep < totalSteps - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const generateExcel = async () => {
    setIsGenerating(true);

    try {
      console.log('Starting Form XIII Excel generation...');
      console.log('Form Data:', formData);

      // Load the template
      const response = await fetch('/templates/Form-XIII.xlsx');
      const arrayBuffer = await response.arrayBuffer();

      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);
      const worksheet = workbook.worksheets[0];

      console.log('Template loaded successfully');

      // Remove ALL background fills from the entire worksheet
      worksheet.eachRow({ includeEmpty: true }, (row) => {
        row.eachCell({ includeEmpty: true }, (cell) => {
          if (cell.fill) {
            cell.fill = null;
          }
        });
      });

      console.log('Background fills removed');

      // Two-pass placeholder replacement strategy

      // Pass 1: Replace form-level fields (organization, contractor)
      console.log('Pass 1: Replacing form-level fields...');
      worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
        row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
          const cellValue = cell.value ? String(cell.value) : '';

          if (cellValue.includes('{{')) {
            const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
            let newValue = cellValue;
            let match;

            while ((match = placeholderRegex.exec(cellValue)) !== null) {
              const fieldName = match[1].trim();
              const normalizedFieldName = fieldName.replace(/\s+/g, '_');

              // Check form-level fields
              if (formData[normalizedFieldName] !== undefined) {
                console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${formData[normalizedFieldName]}"`);
                newValue = newValue.replace(match[0], formData[normalizedFieldName]);
              }
            }

            if (newValue !== cellValue) {
              cell.value = newValue;
            }
          }
        });
      });

      // Pass 2: Replace employee-specific fields for the first employee
      console.log('Pass 2: Replacing employee fields...');
      if (formData.employees && formData.employees.length > 0) {
        const employee = formData.employees[0];

        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
            const cellValue = cell.value ? String(cell.value) : '';

            if (cellValue.includes('{{')) {
              const placeholderRegex = /\{\{\s*([^}]+?)\s*\}\}/g;
              let newValue = cellValue;
              let match;

              while ((match = placeholderRegex.exec(cellValue)) !== null) {
                const fieldName = match[1].trim();
                const normalizedFieldName = fieldName.replace(/\s+/g, '_');

                // Check employee fields
                if (employee[normalizedFieldName] !== undefined) {
                  console.log(`Row ${rowNumber}, Col ${colNumber}: Replacing {{ ${fieldName} }} with "${employee[normalizedFieldName]}"`);
                  newValue = newValue.replace(match[0], employee[normalizedFieldName]);
                }
              }

              if (newValue !== cellValue) {
                cell.value = newValue;
              }
            }
          });
        });
      }

      console.log('All placeholders replaced');

      // Generate Form file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Form-XIII-Employee-Registration-${formData.employees[0]?.emp_name || 'Employee'}-${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Excel file generated and downloaded successfully');

      setTimeout(() => {
        setIsGenerating(false);
        alert('Form XIII - Employee Registration Excel generated successfully!');
      }, 500);

    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(`Error generating Excel: ${error.message}`);
      setIsGenerating(false);
    }
  };

  // Calculate total steps: 1 (organization) + employees
  const totalSteps = 1 + formData.employees.length;
  const progress = ((currentStep + 1) / totalSteps) * 100;

  // Render step content
  const renderStepContent = () => {
    // Step 0: Organization & Contractor
    if (currentStep === 0) {
      return (
        <>
          <div className="form-section">
            <div className="section-header">
              <Building2 size={20} />
              <h3>Organization Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field full-width">
                <label>Organization Name</label>
                <input
                  type="text"
                  value={formData.est_name}
                  onChange={(e) => handleInputChange('est_name', e.target.value)}
                />
              </div>
              <div className="form-field full-width">
                <label>Organization Address</label>
                <textarea
                  value={formData.est_address}
                  onChange={(e) => handleInputChange('est_address', e.target.value)}
                  rows={2}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <div className="section-header">
              <Briefcase size={20} />
              <h3>Contractor Information</h3>
            </div>
            <div className="form-grid">
              <div className="form-field">
                <label>Contractor Name</label>
                <input
                  type="text"
                  value={formData.contractor_name}
                  onChange={(e) => handleInputChange('contractor_name', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>Contractor Address</label>
                <input
                  type="text"
                  value={formData.contractor_address}
                  onChange={(e) => handleInputChange('contractor_address', e.target.value)}
                />
              </div>
            </div>
          </div>
        </>
      );
    }

    // Steps 1 to N: Employee details
    if (currentStep > 0 && currentStep <= formData.employees.length) {
      const employeeIndex = currentStep - 1;
      const employee = formData.employees[employeeIndex];

      return (
        <div className="form-section" style={{ background: 'var(--theme-surface)', border: '1px solid #e5e7eb' }}>
          <div className="section-header" style={{ justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <User size={20} />
              <h3>Employee {employeeIndex + 1} - Registration Details</h3>
            </div>
            {formData.employees.length > 1 && (
              <button
                onClick={() => removeEmployee(employeeIndex)}
                style={{
                  background: '#ef4444',
                  color: 'var(--theme-surface, #ffffff)',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '0.5rem 1rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '0.875rem'
                }}
              >
                <Trash2 size={16} />
                Remove Employee
              </button>
            )}
          </div>
          <div className="form-grid">
            <div className="form-field">
              <label>Employee ID</label>
              <input
                type="text"
                value={employee.emp_id}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_id', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>First Name</label>
              <input
                type="text"
                value={employee.emp_name}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_name', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Last Name</label>
              <input
                type="text"
                value={employee.emp_lname}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_lname', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Age</label>
              <input
                type="number"
                value={employee.emp_age}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_age', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Gender</label>
              <select
                value={employee.emp_gender}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_gender', e.target.value)}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="form-field">
              <label>Designation</label>
              <input
                type="text"
                value={employee.emp_desig}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_desig', e.target.value)}
              />
            </div>
            <div className="form-field full-width">
              <label>Current Address</label>
              <textarea
                value={employee.emp_address}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_address', e.target.value)}
                rows={2}
              />
            </div>
            <div className="form-field full-width">
              <label>Permanent Address</label>
              <textarea
                value={employee.emp_perm_address}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_perm_address', e.target.value)}
                rows={2}
              />
            </div>
            <div className="form-field">
              <label>Date of Joining</label>
              <DateInput
                value={employee.emp_doj}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_doj', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Date of Exit (if applicable)</label>
              <DateInput
                value={employee.emp_doe}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_doe', e.target.value)}
              />
            </div>
            <div className="form-field full-width">
              <label>Nature of Work</label>
              <textarea
                value={employee.emp_nature_work}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_nature_work', e.target.value)}
                rows={2}
              />
            </div>
            <div className="form-field">
              <label>Work Location</label>
              <input
                type="text"
                value={employee.emp_location_work}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_location_work', e.target.value)}
              />
            </div>
            <div className="form-field">
              <label>Termination Reason (if applicable)</label>
              <input
                type="text"
                value={employee.emp_term_reason}
                onChange={(e) => handleEmployeeChange(employeeIndex, 'emp_term_reason', e.target.value)}
              />
            </div>
          </div>
          <button
            onClick={() => {
              addEmployee();
              setCurrentStep(formData.employees.length + 1);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              backgroundColor: '#10b981',
              color: 'var(--theme-surface, #ffffff)',
              border: 'none',
              borderRadius: '6px',
              padding: '0.75rem 1.5rem',
              fontSize: '0.95rem',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            <Plus size={20} />
            Add Another Employee
          </button>
        </div>
      );
    }
  };

  return (
    <div className="appointment-order-container">
      <div className="appointment-header">
        <div>
          <h2 className="appointment-title">Form XIII - Employee Registration</h2>
          <p className="appointment-subtitle">Register of Workmen Employed by Contractor</p>
        </div>
      </div>

      <div className="appointment-content">
        {/* Progress Bar */}
        <div style={{ marginBottom: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Step {currentStep + 1} of {totalSteps}
            </span>
            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>
              {currentStep === 0 ? 'Organization Info' : `Employee ${currentStep}`}
            </span>
          </div>
          <div style={{ width: '100%', height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{
              width: `${progress}%`,
              height: '100%',
              background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
              transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Step Content */}
        {renderStepContent()}

        {/* Navigation Buttons */}
        <div className="form-actions" style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            disabled={currentStep === 0}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.75rem 1.5rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: currentStep === 0 ? '#f8f9fa' : 'var(--theme-surface, #ffffff)',
              color: currentStep === 0 ? '#9ca3af' : '#374151',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: '500'
            }}
          >
            <ChevronLeft size={20} />
            Previous
          </button>

          {currentStep === totalSteps - 1 ? (
            <button
              onClick={generateExcel}
              disabled={isGenerating}
              className={`btn-generate ${isGenerating ? 'generating' : ''}`}
            >
              {isGenerating ? (
                <>
                  <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" opacity="0.25"/>
                    <path d="M12 2a10 10 0 0 1 0 20" opacity="0.75"/>
                  </svg>
                  <span>Generating Excel...</span>
                </>
              ) : (
                <>
                  <FileDown size={20} />
                  <span>Generate Form</span>
                </>
              )}
            </button>
          ) : (
            <button
              onClick={nextStep}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                borderRadius: '6px',
                border: 'none',
                background: 'linear-gradient(to right, #3b82f6, #8b5cf6)',
                color: 'var(--theme-surface, #ffffff)',
                cursor: 'pointer',
                fontSize: '0.95rem',
                fontWeight: '500'
              }}
            >
              Next
              <ChevronRight size={20} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormXIIIMinimumWages;
